import type { Row, RowData, TableFeature } from '@tanstack/react-table';

export interface DataTableRowExpandingOptions<TData extends RowData> {
  /**
   * DataTable의 행 확장 기능 사용 여부와 행별 비활성 상태를 설정합니다.
   *
   * - `true`: 행 확장 기능을 활성화합니다.
   * - `false`: 행 확장 기능을 비활성화합니다.
   * - 함수: 행 확장 기능을 활성화하고 반환값이 `false`인 행의 Chevron을 비활성화합니다.
   *
   * `enableExpanding`, `getRowCanExpand`보다 우선 적용됩니다.
   *
   * @default undefined
   */
  enableRowExpanding?: boolean | ((row: Row<TData>) => boolean);
  /**
   * 행별로 Chevron을 숨길지 결정합니다.
   * 숨긴 행은 확장 컬럼의 공간은 유지하지만 Chevron을 표시하지 않으며 확장 대상에서도 제외됩니다.
   *
   * @default undefined
   */
  hideRowExpanding?: (row: Row<TData>) => boolean;
}

export interface DataTableRowExpandingRow {
  /**
   * 해당 행의 Chevron이 숨김 상태인지 반환합니다.
   * Feature가 등록되지 않은 기존 Table 인스턴스와의 호환을 위해 optional 메서드로 제공합니다.
   */
  getIsExpandingHidden?: () => boolean;
}

declare module '@tanstack/react-table' {
  interface TableOptionsResolved<TData extends RowData>
    extends DataTableRowExpandingOptions<TData> {}

  // TanStack 원본 선언과 제네릭 이름이 같아야 합니다.
  // eslint-disable-next-line unused-imports/no-unused-vars
  interface Row<TData extends RowData> extends DataTableRowExpandingRow {}
}

export const RowExpanding: TableFeature<unknown> = {
  createRow: (row, table) => {
    const getLegacyCanExpand = row.getCanExpand;

    row.getIsExpandingHidden = () => table.options.hideRowExpanding?.(row) ?? false;
    row.getCanExpand = () => {
      if (row.getIsExpandingHidden?.()) return false;

      const enableRowExpanding = table.options.enableRowExpanding;
      if (typeof enableRowExpanding === 'function') return enableRowExpanding(row);
      if (enableRowExpanding !== undefined) {
        return enableRowExpanding && (table.options.getSubRows ? row.subRows.length > 0 : true);
      }

      if (!table.options.getRowCanExpand && !table.options.getSubRows) {
        return table.options.enableExpanding ?? false;
      }
      return getLegacyCanExpand();
    };
  },
};
